import React from 'react';
import { useTranslation } from 'react-i18next';

import { Container, SectionHeading } from '../ui';
import { pillAccredited } from '../../assets';

/**
 * The designer set the strip to 12 seconds for the five logos on the "Logos
 * Track" node — 2.4 seconds each. Keeping the rate rather than the total is
 * what lets the list grow without the strip speeding up.
 */
const SECONDS_PER_LOGO = 12 / 5;

/**
 * One logo in a fixed slot.
 *
 * The design gives every slot the same 250×117 box but lets each mark keep its
 * own height inside it — 117, 104, 62, 74 and 74 — so a wide wordmark and a
 * tall roundel both read at their natural weight. Forcing one height instead
 * would shrink the wordmarks and blow up the roundels.
 *
 * The slot scales down on small screens; `object-contain` keeps every aspect
 * ratio intact as it does.
 */
const LogoItem = ({ item, ariaHidden }) => (
    <li
        className={
            'tw-flex tw-h-[78px] tw-w-[168px] tw-shrink-0 tw-items-center tw-justify-center ' +
            'tw-me-[34px] md:tw-h-[117px] md:tw-w-[250px] md:tw-me-[52px]'
        }
        aria-hidden={ariaHidden || undefined}
    >
        <img
            src={item.logoUrl}
            alt={ariaHidden ? '' : item.name}
            loading="lazy"
            className="tw-max-h-full tw-max-w-full tw-object-contain"
        />
    </li>
);

/**
 * "معتمدون من قِبل" — the accrediting bodies.
 *
 * Two shapes from one component, because the design uses both:
 *   scroll — the home page, logos drifting past without end
 *   static — the certificate page, a single centred mark
 *
 * The marquee is CSS only. The list is rendered twice inside a track that
 * animates to -50%: at the end of each cycle the second copy sits exactly where
 * the first began, so the reset is invisible and no JavaScript ticks per frame.
 * The duplicate is `aria-hidden`, or a screen reader would read every logo twice.
 *
 * @param {{id: string, name: string, logoUrl: string}[]} items
 */
const AccreditedBy = ({
    items = [],
    variant = 'scroll',
    pill,
    title,
    lead,
    // The certificate page anchors its section nav here, and has to hold the
    // sticky header clear of it. Both are ignored by the marquee, which is not
    // a link target on any page that uses it.
    id,
    scrollMarginTop,
    className = '',
}) => {
    const { t, i18n } = useTranslation();

    if (!items.length) return null;

    /*
     * How many logos one half of the track holds.
     *
     * The track is two identical halves and slides by exactly half its width,
     * so the seam is invisible — but only if a half is at least as wide as the
     * screen. Five logos come to about 1510px, which leaves a bare stretch on a
     * 1920px display before the second half arrives. Repeating the list up to a
     * minimum count fills the strip whatever the screen and however few bodies
     * the backend returns.
     */
    const MIN_SLOTS_PER_HALF = 12;
    const loop = Array.from(
        { length: Math.max(items.length, MIN_SLOTS_PER_HALF) },
        (_, index) => items[index % items.length]
    );

    const heading = (
        <SectionHeading
            pill={pill ?? t('v2.accredited.pill')}
            pillIcon={<img src={pillAccredited} alt="" aria-hidden className="tw-h-4 tw-w-4" />}
            title={title ?? t('v2.accredited.title')}
            lead={lead ?? t('v2.accredited.lead')}
        />
    );

    /*
     * The certificate page's version of the strip.
     *
     * The same bodies, but the design there is a plainer thing: no pill, no
     * lead, a tinted ground rather than white, and the marks standing still at
     * their own widths instead of drifting through a fixed slot.
     *
     * The design draws a single body at 117px tall. Certificates that carry
     * four or five wordmarks put that height across a row 1700px wide, which
     * reads as a wall of logos rather than as a credit line — so the marks are
     * set at 80px and keep their own widths, which is the size the row holds at
     * whatever count the backend returns.
     */
    if (variant === 'static') {
        return (
            <section
                id={id}
                aria-labelledby={id ? `${id}-heading` : undefined}
                className={`tw-bg-v2-ground-100 tw-py-16 md:tw-pb-14 md:tw-pt-20 ${className}`}
                style={scrollMarginTop ? { scrollMarginTop } : undefined}
            >
                <Container className="tw-flex tw-flex-col tw-items-center">
                    <h2
                        id={id ? `${id}-heading` : undefined}
                        className="tw-text-center tw-text-3xl tw-font-bold tw-text-v2-text-strong md:tw-text-v2-h2"
                    >
                        {title ?? t('v2.accredited.title')}
                    </h2>

                    <ul className="tw-mt-10 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-14 tw-gap-y-8">
                        {items.map(item => (
                            <li key={item.id} className="tw-flex tw-items-center tw-justify-center">
                                <img
                                    src={item.logoUrl}
                                    alt={item.name}
                                    loading="lazy"
                                    className="tw-h-[52px] tw-w-auto tw-max-w-full tw-object-contain md:tw-h-[80px]"
                                />
                            </li>
                        ))}
                    </ul>
                </Container>
            </section>
        );
    }

    return (
        <section className={`tw-bg-white tw-py-16 md:tw-py-20 ${className}`}>
            <Container>{heading}</Container>

            {/* Full-bleed: the strip runs past the container's edges, and the
                mask fades it out instead of cutting it off mid-logo. */}
            {/*
              * The window is pinned to `ltr`, and the track inside it with it.
              *
              * `w-max` makes the track wider than this window, and the overflow
              * falls on the side the direction ends at — to the left under
              * `rtl`. The keyframe translates by -50%, physically leftward, so
              * an rtl track walks away from its own spare copy: the logos leave
              * through the left edge and nothing follows them. Under `ltr` the
              * spare copy sits on the right, which is the side the strip pulls
              * the next logo in from, so the seam closes at the moment the
              * first copy clears the left edge.
              *
              * It has to sit here rather than on the track: an over-wide block
              * is placed against its *containing block's* inline start, so the
              * window's direction is what decides which way the track hangs.
              *
              * Only the box order is pinned — `animationDirection` below still
              * decides which way it drifts, and the slots hold images, so
              * nothing here reads as text.
              */}
            <div
                dir="ltr"
                className="tw-relative tw-mt-12 tw-overflow-hidden"
                style={{
                    maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
                    WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
                }}
            >
                <ul
                    // Spacing lives on the items, not in a `gap` here. With a gap
                    // the track is [half][gap][half], so half its width falls in
                    // the middle of that gap and every loop jumps by half a gap.
                    // A trailing margin makes each slot exactly (width + gap)
                    // wide, so half the track is precisely where the second half
                    // starts.
                    className="tw-flex tw-w-max tw-items-center tw-animate-v2-marquee motion-reduce:tw-animate-none hover:[animation-play-state:paused]"
                    style={{
                        // Arabic reads right to left, so the strip drifts that way too.
                        animationDirection: i18n.language?.startsWith('ar') ? 'normal' : 'reverse',
                        // The designer's 12 seconds covered five logos. Padding
                        // the half to fill the screen would make the same 12
                        // seconds two and a half times faster, so the duration
                        // grows with the count and the pace stays theirs.
                        animationDuration: `${(loop.length * SECONDS_PER_LOGO).toFixed(1)}s`,
                    }}
                >
                    {loop.map((item, index) => (
                        <LogoItem key={`a-${index}`} item={item} />
                    ))}
                    {loop.map((item, index) => (
                        <LogoItem key={`b-${index}`} item={item} ariaHidden />
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default AccreditedBy;
